import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedDate } from 'react-intl';
import { Button, List, ListItem, SwipeableDrawer, Typography } from '@mui/material';
import FuseScrollbars from '@fuse/core/FuseScrollbars';

import withReducer from 'app/store/withReducer';
import useCanOpenPage from 'app/providers/acl/useCanOpenPage';
import { SquareChip, useSettings, useUserData, Utils, Text } from 'app/main/components';

import reducer from './store';
import { closeLastActionsPanel, toggleLastActionsPanel } from './store/stateSlice';

function LastActionsPanelListItem({ action, handleClose }: { action: any; handleClose: VoidFunction }) {
	const { t } = useSettings();
	const { canOpenDetailsPage } = useCanOpenPage();

	const url = canOpenDetailsPage(action.logs_table, action.logs_table_uid);

	return (
		<ListItem disableGutters>
			<div>
				<div onClick={() => handleClose()}>
					<Text className="whitespace-normal leading-normal" variant="subtitle2" to={url}>
						{action.item_title}
					</Text>
				</div>
				<Typography>
					<FormattedDate
						value={Utils.parseDate(action.logs_date)?.toDate()}
						year="numeric"
						month="long"
						day="numeric"
						hour="numeric"
						minute="numeric"
						second="numeric"
					/>
				</Typography>
				<SquareChip label={action.lgty_title} backgroundColor={action.lgty_color} />
				<SquareChip label={t(`MODULE_${action.logs_table}_NAME_SINGULAR`)} />
			</div>
		</ListItem>
	);
}

function LastActionsPanel() {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const panelState = useSelector(({ lastActionsPanel }: any) => lastActionsPanel.state);

	const [lastActions, setLastActions] = React.useState<any[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [currentPage, setCurrentPage] = React.useState(1);

	function handleClose() {
		dispatch(closeLastActionsPanel());
	}

	const getLastActions = React.useCallback(
		nPage => {
			setCurrentPage(nPage);
			setIsLoading(true);
			Utils.executeStored({
				action: 'st_sys_logs_list',
				dispatch,
				params: {
					n_page: nPage,
					n_rows: 30,
					filters: [
						{
							filt_field: 'logs_user_uid',
							filt_clause: 'equals',
							filt_value: userData.user_uid
						}
					]
				},
				successHandler: response => {
					setLastActions(state => (nPage > 1 ? [...state, ...response.data.list] : response.data.list));
					setIsLoading(false);
				},
				errorHandler: () => {
					setIsLoading(false);
				}
			});
		},
		[dispatch, userData.user_uid]
	);

	React.useEffect(() => {
		if (panelState) {
			getLastActions(1);
		}
	}, [getLastActions, panelState]);

	return (
		<SwipeableDrawer
			open={panelState}
			anchor="right"
			onOpen={_ev => {}}
			onClose={_ev => dispatch(toggleLastActionsPanel())}
			disableSwipeToOpen
			sx={theme => ({
				'.MuiDrawer-paper': {
					backgroundColor: theme.palette.background.default,
					width: '100%',
					maxWidth: 400
				}
			})}
		>
			<FuseScrollbars className="p-16">
				<div className="flex flex-col">
					<div className="mb-8 flex items-center justify-between pt-4">
						<Typography className="text-28 font-semibold leading-none">
							{t('RECENT_ACTIVITY_TITLE')}
						</Typography>
						<Button onClick={handleClose}>{t('DIALOG_ACTION_CLOSE')}</Button>
					</div>
					{(!isLoading || currentPage > 1) && (
						<List className="p-0">
							{lastActions &&
								lastActions.map((action, index) => (
									<LastActionsPanelListItem key={index} action={action} handleClose={handleClose} />
								))}
						</List>
					)}
					<Button
						onClick={() => {
							const nextPage = currentPage + 1;
							getLastActions(nextPage);
						}}
						loading={isLoading}
						loadingPosition="center"
					>
						{t('RECENT_ACTIVITY_LOAD_MORE_BUTTON')}
					</Button>
				</div>
			</FuseScrollbars>
		</SwipeableDrawer>
	);
}

export default withReducer('lastActionsPanel', reducer)(React.memo(LastActionsPanel));
